import { ChangeEvent, useEffect, useRef } from "react";
import { FileUp, FolderUp } from "lucide-react";
import { Button } from "primereact/button";
import { Upload, useUpload } from "@/providers/UploadProvider";
import { v4 } from "uuid";
import { fileUploadsPreprocess } from "@/utils/common";
import { useToastStore } from "@/stores/toast.store";

interface Props {
  accept?: "image" | "font";
  type?: "folder" | "file";
  disabled?: boolean;
  handler?: (data: File[], onProgress: (data: string) => void) => Promise<void>;
}

export default function Uploader({
  accept,
  type = "folder",
  disabled,
  handler,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const startUpload = useUpload().startUpload;
  const addToast = useToastStore((state) => state.addToast);

  useEffect(() => {
    if (!inputRef.current || type !== "folder") return;
    inputRef.current.webkitdirectory = true;
  }, [type]);

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!handler) return;

    const fileList = event.target.files;
    if (!fileList) return;

    const files: File[] = Array.from(fileList);
    event.target.value = "";

    const { validFiles, invalidFiles } = await fileUploadsPreprocess(
      files,
      accept,
    );

    if (invalidFiles.length > 0) {
      addToast({
        severity: "error",
        summary: "Error",
        detail: `Unsupported ${accept} file${invalidFiles.length > 1 ? "s" : ""}: ${invalidFiles.map((f) => f.name).join(", ")}`,
      });
      return;
    }

    const total = validFiles.length;
    if (total === 0) return;

    const folderName = files[0].webkitRelativePath.split("/")[0];
    const totalFilesString = `${total} file${total > 1 ? "s" : ""}`;

    const upload: Upload = {
      id: v4(),
      name: type === "folder" ? `${folderName}` : `${totalFilesString}`,
      type,
      status: "pending",
      data: validFiles,
      handler,
    };
    startUpload(upload);
  };

  return (
    <>
      <Button
        style={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
        severity="info"
        tooltip={type === "folder" ? "Upload folder" : "Upload files"}
        tooltipOptions={{ position: "top" }}
        disabled={disabled}
        icon={type === "folder" ? <FolderUp size={18} /> : <FileUp size={18} />}
        onClick={() => inputRef.current?.click()}
      />
      <input
        ref={inputRef}
        type="file"
        accept={`${accept}/*`}
        multiple
        hidden
        onChange={onChange}
      />
    </>
  );
}
